/**
 * Форма создания / редактирования шаблона анализа менеджера
 * (ADR-019, TM-5743cb35). Используется в `/admin/analysis-templates`.
 *
 * Поля: title (255), prompt_text (TEXT), is_active (bool).
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { createAnalysisTemplate, updateAnalysisTemplate } from "@/api/lookups";
import type { AnalysisTemplateResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const schema = z.object({
  title: z.string().min(1).max(255),
  prompt_text: z.string().min(1),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

export interface AnalysisTemplateFormProps {
  open: boolean;
  existing: AnalysisTemplateResponse | null;
  onClose: () => void;
  afterSubmit: () => Promise<unknown> | void;
}

export function AnalysisTemplateForm({
  open,
  existing,
  onClose,
  afterSubmit,
}: AnalysisTemplateFormProps) {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      prompt_text: "",
      is_active: true,
    },
  });

  // При открытии или смене existing — подставляем значения в форму.
  useEffect(() => {
    if (!open) return;
    if (existing === null) {
      form.reset({ title: "", prompt_text: "", is_active: true });
    } else {
      form.reset({
        title: existing.title,
        prompt_text: existing.prompt_text,
        is_active: existing.is_active,
      });
    }
  }, [open, existing, form]);

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      existing === null
        ? createAnalysisTemplate(v)
        : updateAnalysisTemplate(existing.id, v),
    onSuccess: async () => {
      await afterSubmit();
      onClose();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {existing === null
              ? t("analysisTemplates.createButton")
              : t("common.edit")}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          data-testid="analysis-template-form"
        >
          <div>
            <Label htmlFor="atpl-title">
              {t("analysisTemplates.fields.title")}
            </Label>
            <Input
              id="atpl-title"
              {...form.register("title")}
              data-testid="analysis-template-title"
            />
            {form.formState.errors.title !== undefined ? (
              <p className="text-xs text-destructive">
                {t("analysisTemplates.fields.title")}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="atpl-prompt">
              {t("analysisTemplates.fields.promptText")}
            </Label>
            <textarea
              id="atpl-prompt"
              className="min-h-32 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              {...form.register("prompt_text")}
              data-testid="analysis-template-prompt"
            />
            {form.formState.errors.prompt_text !== undefined ? (
              <p className="text-xs text-destructive">
                {t("analysisTemplates.fields.promptText")}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <input
              id="atpl-active"
              type="checkbox"
              {...form.register("is_active")}
              data-testid="analysis-template-active"
            />
            <Label htmlFor="atpl-active">
              {t("analysisTemplates.fields.isActive")}
            </Label>
          </div>
          {mutation.isError ? (
            <p className="text-xs text-destructive" role="alert">
              {t("analysisTemplates.saveError")}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("analysisTemplates.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              data-testid="analysis-template-save"
            >
              {mutation.isPending
                ? t("common.saving")
                : t("analysisTemplates.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
